import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { IMPORTANT_TIMEZONES, formatTimezoneLabel, getDefaultTimezone } from '../constants/timezones';
import '../styles/design-system.css';
import '../styles/Dashboard.css';

function BrandSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();

  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socialError, setSocialError] = useState(searchParams.get('social_error') || '');
  const [socialSuccess, setSocialSuccess] = useState(searchParams.get('social_success') || '');
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [selectedFacebookPageId, setSelectedFacebookPageId] = useState('');
  const [selectedInstagramAccountId, setSelectedInstagramAccountId] = useState('');
  const [loadingPageOptions, setLoadingPageOptions] = useState(false);
  const [savingPageSelection, setSavingPageSelection] = useState(false);
  const [schedule, setSchedule] = useState({ auto_post_enabled: false, auto_post_time: '09:00', auto_post_timezone: getDefaultTimezone() });

  useEffect(() => {
    fetchBrandData();
  }, [id]);

  const fetchBrandData = async () => {
    try {
      const response = await api.get(`/api/brands/${id}`);
      const brandData = response.data;
      setBrand(brandData);
      setSchedule({
        auto_post_enabled: brandData.auto_post_enabled || false,
        auto_post_time: brandData.auto_post_time || '09:00',
        auto_post_timezone: brandData.auto_post_timezone || getDefaultTimezone(),
      });
      const fbConnected = brandData.facebook_connected || false;
      const igConnected = brandData.instagram_connected || false;
      setFacebookConnected(fbConnected);
      setInstagramConnected(igConnected);
      if (fbConnected || igConnected) {
        await fetchPageOptions();
      }
    } catch (error) {
      console.error('Failed to fetch brand settings:', error);
      alert('Failed to load brand settings');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const connectUrl = (type) => {
    const base = (api.defaults.baseURL || window.location.origin).replace(/\/$/, '');
    return `${base}/brand/${id}/connect/${type}`;
  };

  const fetchPageOptions = async () => {
    setLoadingPageOptions(true);
    try {
      const response = await api.get(`/brand/${id}/social-pages`);
      const data = response.data || {};
      setFacebookPages(data.facebook_pages || []);
      setInstagramAccounts(data.instagram_accounts || []);
      setSelectedFacebookPageId(data.selected_facebook_page_id || '');
      setSelectedInstagramAccountId(data.selected_instagram_id || '');
    } catch (error) {
      setFacebookPages([]);
      setInstagramAccounts([]);
      setSelectedFacebookPageId('');
      setSelectedInstagramAccountId('');
      setSocialSuccess('');
      setSocialError(error.response?.data?.detail || 'Failed to load your Facebook/Instagram pages');
    } finally {
      setLoadingPageOptions(false);
    }
  };

  const handleSavePageSelection = async () => {
    if (!selectedFacebookPageId && !selectedInstagramAccountId) {
      alert('Please select at least one page/account');
      return;
    }

    setSavingPageSelection(true);
    try {
      await api.post(`/brand/${id}/social-pages/select`, {
        facebook_page_id: selectedFacebookPageId || null,
        instagram_account_id: selectedInstagramAccountId || null,
      });
      setSocialError('');
      setSocialSuccess('Posting targets updated successfully');
      await fetchBrandData();
    } catch (error) {
      setSocialSuccess('');
      setSocialError(error.response?.data?.detail || 'Failed to save page selection');
    } finally {
      setSavingPageSelection(false);
    }
  };

  const handleDisconnect = async (type) => {
    if (!window.confirm(`Disconnect ${type} for this brand?`)) return;
    try {
      const res = await api.post(`/brand/${id}/disconnect/${type}`);
      setSocialError('');
      setSocialSuccess(res.data?.message || 'Disconnected successfully');
      fetchBrandData();
    } catch (error) {
      setSocialSuccess('');
      setSocialError(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('auto_post_enabled', schedule.auto_post_enabled);
      formData.append('auto_post_time', schedule.auto_post_time);
      formData.append('auto_post_timezone', schedule.auto_post_timezone);
      await api.post(`/brand/${id}/schedule`, formData);
      alert('Schedule saved!');
    } catch (error) {
      alert('Failed to save schedule: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="ps-loading">
        <div className="ps-spinner" />
        <span className="ps-loading-text">Loading settings…</span>
      </div>
    );
  }

  const timezoneOptions = schedule.auto_post_timezone && !IMPORTANT_TIMEZONES.includes(schedule.auto_post_timezone)
    ? [schedule.auto_post_timezone, ...IMPORTANT_TIMEZONES]
    : IMPORTANT_TIMEZONES;

  return (
    <div className="ps-page">
      <nav className="ps-nav">
        <div className="ps-container">
          <div className="ps-nav__inner">
            <Link to="/dashboard" className="ps-nav__logo">
              Postrify <span className="ps-nav__logo-dot" />
            </Link>
            <div className="ps-nav__right">
              <Link to={`/brand/${id}`} className="ps-btn ps-btn--ghost ps-btn--sm">← View Posts</Link>
              <button onClick={logout} className="ps-btn ps-btn--secondary ps-btn--sm">Log out</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="ps-container">
        <div className="ps-page-header">
          <div>
            <h1 className="ps-page-header__title">{brand?.name} Settings</h1>
            <p className="ps-page-header__sub">Manage social connections and auto-post schedule</p>
          </div>
        </div>

        {socialError && <div className="page-alert page-alert--error">⚠ {socialError}</div>}
        {socialSuccess && <div className="page-alert page-alert--success">✓ {socialSuccess}</div>}

        <div className="ps-card">
          <div className="ps-card__title"><span className="ps-card__title-icon">📱</span> Social Accounts</div>
          <div className="social-panel">
            <div className="social-row">
              <div className="social-row__left">
                <span className="social-row__icon">🇫</span>
                <div>
                  <div className="social-row__name">Facebook</div>
                  <span className={`ps-social-status ps-social-status--${facebookConnected ? 'connected' : 'disconnected'}`}>
                    <span className="ps-status-dot" />
                    {facebookConnected ? 'Connected' : 'Not connected'}
                  </span>
                </div>
              </div>
              <div className="social-row__actions">
                {!facebookConnected
                  ? <a href={connectUrl('facebook-only')} className="ps-btn ps-btn--primary ps-btn--sm">Connect</a>
                  : <button onClick={() => handleDisconnect('facebook')} className="ps-btn ps-btn--secondary ps-btn--sm">Disconnect</button>}
              </div>
            </div>

            <div className="social-row">
              <div className="social-row__left">
                <span className="social-row__icon">📷</span>
                <div>
                  <div className="social-row__name">Instagram</div>
                  <span className={`ps-social-status ps-social-status--${instagramConnected ? 'connected' : 'disconnected'}`}>
                    <span className="ps-status-dot" />
                    {instagramConnected ? 'Connected' : 'Not connected'}
                  </span>
                </div>
              </div>
              <div className="social-row__actions">
                {!instagramConnected
                  ? <a href={connectUrl('instagram-only')} className="ps-btn ps-btn--primary ps-btn--sm">Connect</a>
                  : <button onClick={() => handleDisconnect('instagram')} className="ps-btn ps-btn--secondary ps-btn--sm">Disconnect</button>}
              </div>
            </div>

            {(!facebookConnected || !instagramConnected) && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', paddingTop: '4px' }}>
                <a href={connectUrl('facebook')} className="ps-btn ps-btn--success ps-btn--sm">Connect Facebook & Instagram</a>
              </div>
            )}
          </div>
        </div>

        {(facebookConnected || instagramConnected) && (
          <div className="ps-card">
            <div className="ps-card__title"><span className="ps-card__title-icon">🎯</span> Posting Targets</div>
            <p className="ps-page-header__sub" style={{ marginBottom: '12px' }}>
              Choose which Facebook Page and Instagram account should receive published posts.
            </p>
            {loadingPageOptions ? (
              <p className="ps-page-header__sub">Loading your pages…</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <div className="ps-field" style={{ flex: 1, margin: 0, minWidth: '240px' }}>
                    <label className="ps-label">Facebook Page</label>
                    <select
                      className="ps-input"
                      value={selectedFacebookPageId}
                      onChange={(e) => setSelectedFacebookPageId(e.target.value)}
                      disabled={facebookPages.length === 0}
                    >
                      <option value="">Select Facebook page</option>
                      {facebookPages.map((page) => (
                        <option key={page.id} value={page.id}>{page.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ps-field" style={{ flex: 1, margin: 0, minWidth: '240px' }}>
                    <label className="ps-label">Instagram Account</label>
                    <select
                      className="ps-input"
                      value={selectedInstagramAccountId}
                      onChange={(e) => setSelectedInstagramAccountId(e.target.value)}
                      disabled={instagramAccounts.length === 0}
                    >
                      <option value="">Select Instagram account</option>
                      {instagramAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.username} ({account.page_name})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  className="ps-btn ps-btn--secondary ps-btn--sm"
                  onClick={handleSavePageSelection}
                  disabled={savingPageSelection}
                >
                  {savingPageSelection ? 'Saving…' : 'Save Posting Targets'}
                </button>
              </>
            )}
          </div>
        )}

        <div className="ps-card" style={{ marginBottom: '40px' }}>
          <div className="ps-card__title"><span className="ps-card__title-icon">📅</span> Auto-Post Schedule</div>
          <form onSubmit={handleScheduleSubmit}>
            <div className="ps-field">
              <label className="ps-checkbox-row">
                <input
                  type="checkbox"
                  checked={schedule.auto_post_enabled}
                  onChange={(e) => setSchedule({ ...schedule, auto_post_enabled: e.target.checked })}
                />
                Enable daily auto-posting
              </label>
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <div className="ps-field" style={{ margin: 0 }}>
                <label className="ps-label">Post Time</label>
                <input
                  className="ps-input"
                  type="time"
                  value={schedule.auto_post_time}
                  onChange={(e) => setSchedule({ ...schedule, auto_post_time: e.target.value })}
                  required
                />
              </div>
              <div className="ps-field" style={{ flex: 1, margin: 0, minWidth: '200px' }}>
                <label className="ps-label">Timezone</label>
                <select
                  className="ps-input"
                  value={schedule.auto_post_timezone}
                  onChange={(e) => setSchedule({ ...schedule, auto_post_timezone: e.target.value })}
                  required
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz} value={tz}>{formatTimezoneLabel(tz)}</option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" className="ps-btn ps-btn--secondary ps-btn--sm">Save Schedule</button>
          </form>
        </div>
      </div>

      <footer className="ps-footer">
        <div className="ps-container">
          © 2024 Postrify · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

export default BrandSettings;
